import { parse, HTMLElement, TextNode, Node } from 'node-html-parser';
import DOMPurify from 'isomorphic-dompurify';

export function sanitizeHtml(
  html: string,
  addTags?: string[],
  addAttr?: string[]
) {
  if (!html) return '';
  return DOMPurify.sanitize(html, { ADD_TAGS: addTags, ADD_ATTR: addAttr });
}

/**
 * Санитизация HTML-эмбеда (видео/аудио) с сохранением <iframe>, но без опасных
 * конструкций: DOMPurify вырезает обработчики событий (onerror/onload) и
 * скрипты. Прежде embed вставлялся в dangerouslySetInnerHTML сырым — это была
 * поверхность stored XSS, если поле заполняет менее доверенный редактор CMS.
 */
export function sanitizeEmbed(html: string): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: [
      'allow',
      'allowfullscreen',
      'frameborder',
      'scrolling',
      'loading',
      'referrerpolicy',
      'title',
      'width',
      'height'
    ]
  });
}

export function stripHtmlAttributes(
  html: string,
  attrsToRemove: string[]
): string {
  if (!html) return '';

  const regexPatterns = attrsToRemove.map(
    attr => new RegExp(`\\s+${attr}=["'][^"']*["']`, 'g')
  );

  let cleanedHtml = html;

  regexPatterns.forEach(pattern => {
    cleanedHtml = cleanedHtml.replace(pattern, '');
  });

  return cleanedHtml;
}

export function stripHtmlTags(html: string, allowedTags: string[] = []) {
  if (!html) return '';

  const allowedTagsPattern =
    allowedTags.length > 0
      ? allowedTags.map(tag => tag.toLowerCase()).join('|')
      : '';

  const regex =
    allowedTags.length > 0
      ? new RegExp(`<(?!\/?${allowedTagsPattern}\\b)[^>]+>`, 'gi')
      : /<[^>]+>/g;

  let cleaned = html.replace(regex, '');

  cleaned = cleaned
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&mdash;/g, '—');

  return cleaned;
}

export function removeNonBreakingSpaces(text: string): string {
  if (!text) return '';
  return text.replace(/\u00A0/g, ' ').replace(/&nbsp;/g, ' ');
}

export function addNonBreakingSpaces(text: string): string {
  if (!text) return '';

  const shortWords = new Set([
    'в',
    'к',
    'на',
    'за',
    'об',
    'от',
    'по',
    'со',
    'у',
    'из',
    'без',
    'до',
    'при',
    'про',
    'с',
    'о',
    'мы',
    'во'
  ]);

  const processText = (text: string): string => {
    const parts = text.split(/(\s+|[.,!?])/g).filter(Boolean);

    for (let i = 0; i < parts.length - 1; i++) {
      const currentWord = parts[i].trim();

      if (!currentWord || /^[.,!?\s]+$/.test(currentWord)) continue;

      if (shortWords.has(currentWord.toLowerCase()) || currentWord.length < 3) {
        let nextIndex = i + 1;
        while (nextIndex < parts.length && parts[nextIndex].trim() === '') {
          nextIndex++;
        }

        if (nextIndex < parts.length) {
          for (let j = i + 1; j < nextIndex; j++) {
            if (parts[j].trim() === '') {
              parts[j] = '\u00A0';
            }
          }
        }
      }
    }

    return parts.join('');
  };

  try {
    const root = parse(text);

    const processNode = (node: Node) => {
      if (node.nodeType === 3) {
        const textNode = node as TextNode;

        textNode.textContent = processText(textNode.textContent);
      } else if (node.nodeType === 1) {
        const elementNode = node as HTMLElement;
        if (
          elementNode.tagName?.toLowerCase() === 'script' ||
          elementNode.tagName?.toLowerCase() === 'style'
        ) {
          return;
        }
        elementNode.childNodes.forEach(processNode);
      }
    };

    processNode(root);
    return root.toString();
  } catch (error) {
    return text.replace(/>([^<]+)</g, (match, textContent) => {
      return '>' + processText(textContent) + '<';
    });
  }
}

export function defaultProcessText(text: string) {
  return addNonBreakingSpaces(
    removeNonBreakingSpaces(
      stripHtmlAttributes(sanitizeHtml(text, [], ['target']), ['class'])
    )
  );
}

// То же, что defaultProcessText, но вырезает теги <a> (видимый текст ссылок
// сохраняется). Нужно для контента, который рендерится внутри карточки-ссылки
// (<Link>): вложенный <a> внутри <a> — невалидный HTML, и в React 19 это ломает
// гидратацию.
export function defaultProcessTextWithoutLinks(text: string) {
  if (!text) return '';
  const sanitized = DOMPurify.sanitize(text, {
    ADD_ATTR: ['target'],
    FORBID_TAGS: ['a']
  });
  return addNonBreakingSpaces(
    removeNonBreakingSpaces(stripHtmlAttributes(sanitized, ['class']))
  );
}

/**
 * Превращает HTML в чистый текст: удаляет всю разметку (в т.ч. ссылки —
 * остаётся только видимый текст), декодирует HTML-сущности (&laquo;, &mdash;,
 * &nbsp; и т.п.) и нормализует пробелы вокруг пунктуации.
 */
export function stripToPlainText(html: string): string {
  if (!html) return '';

  // Теги заменяем пробелами (убираем ссылки/разметку, но сохраняем границы
  // блоков, чтобы текст не склеивался), затем через парсер получаем чистый
  // текст с корректно декодированными HTML-сущностями. В конце убираем
  // пробелы, случайно возникшие на месте инлайновых тегов вокруг пунктуации
  // (например «дела ?» из «дела</strong>?»).
  return parse(html.replace(/<[^>]+>/g, ' '))
    .textContent.replace(/\s+/g, ' ')
    .replace(/\s+([.,!?;:»)\]])/g, '$1')
    .replace(/([«(\[])\s+/g, '$1')
    .trim();
}

/**
 * Извлекает первое предложение из HTML-строки: чистит разметку и отсекает текст
 * по первому разделителю «.», «!» или «?» (разделителем считается знак, за
 * которым идёт пробел или конец строки — чтобы не резать по «3.5» и подобным).
 * Завершающий знак препинания сохраняется.
 */
export function getFirstSentence(html: string): string {
  const text = stripToPlainText(html);

  if (!text) return '';

  const match = text.match(/[.!?](\s|$)/);

  if (!match || match.index === undefined) return text;

  return text.slice(0, match.index + 1).trim();
}
